import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PreLoading } from "@tripian/react";
import Model from "@tripian/model";
import { api } from "@tripian/core";
import useTranslate from "../../hooks/useTranslate";
import { CITY_INFO } from "../../constants/ROUTER_PATH_TITLE";
import VistorTourInfo from "../TourInfo/VistorTourInfo/VistorTourInfo";
import VictoryProductInfo from "../TourInfo/VictoryProductInfo/VictoryProductInfo";
import AppNav from "../../App/AppNav/AppNav";
import useViatorApi from "../../hooks/useViatorApi";

// Parse Viator URL to extract tourId and destinationId
// Example: https://www.viator.com/tours/Chicago/Field-Museum/d673-38510P3?pid=...
// Returns: { tourId: "38510P3", destinationId: 673 }
const parseViatorUrl = (url: string): { tourId: string; destinationId: number } | null => {
    try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        // Match pattern like d673-38510P3 at the end of path
        const match = pathname.match(/d(\d+)-([A-Z0-9]+)$/i);
        if (match && match[1] && match[2]) {
            return {
                destinationId: parseInt(match[1]),
                tourId: match[2]
            };
        }
        return null;
    } catch {
        return null;
    }
};

// Parse Victory (events365) URL to extract eventId
// Example: https://www.events365.com/events/ticket/3037990?listingsType=event&...
// Returns: { eventId: "3037990" }
const parseVictoryUrl = (url: string): { eventId: string } | null => {
    try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        // Match pattern like /events/ticket/3037990
        const match = pathname.match(/\/events\/ticket\/(\d+)/);
        if (match && match[1]) {
            return { eventId: match[1] };
        }
        return null;
    } catch {
        return null;
    }
};

const TourDetailPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const cityId = searchParams.get("cityId");
    const provider = searchParams.get("provider");
    const tourId = searchParams.get("tourId");
    const externalUrl = searchParams.get("url"); // External URL parameter (Viator or Victory)
    const startDate = searchParams.get("startDate") || undefined;
    const endDate = searchParams.get("endDate") || undefined;
    const adults = searchParams.get("adults") ? parseInt(searchParams.get("adults")!) : undefined;
    const children = searchParams.get("children") ? parseInt(searchParams.get("children")!) : undefined;
    const lat = searchParams.get("lat") ? parseFloat(searchParams.get("lat")!) : undefined;
    const lng = searchParams.get("lng") ? parseFloat(searchParams.get("lng")!) : undefined;

    const [isProcessingUrl, setIsProcessingUrl] = useState(false);
    const [processingError, setProcessingError] = useState<string | null>(null);

    const navigate = useNavigate();
    const { t } = useTranslate();

    const { fetchDestinations } = useViatorApi();

    // Process external URL (Viator or Victory) and redirect with proper parameters
    const processExternalUrl = useCallback(async (url: string) => {
        setIsProcessingUrl(true);
        setProcessingError(null);

        try {
            // Check if it's a Victory (events365) URL
            const victoryData = parseVictoryUrl(url);
            if (victoryData) {
                const newParams = new URLSearchParams();
                newParams.set("provider", Model.PROVIDER_ID.VICTORY.toString());
                newParams.set("tourId", victoryData.eventId);
                setSearchParams(newParams, { replace: true });
                setIsProcessingUrl(false);
                return;
            }

            // Check if it's a Viator URL
            const viatorData = parseViatorUrl(url);
            if (viatorData) {
                const { tourId: parsedTourId, destinationId: destId } = viatorData;

                const destinations = await fetchDestinations();
                if (!destinations || destinations.length === 0) {
                    setProcessingError("Could not fetch destinations");
                    setIsProcessingUrl(false);
                    return;
                }

                const matchingDest = destinations.find(d => d.destinationId === destId);
                if (!matchingDest) {
                    setProcessingError("Could not find matching destination");
                    setIsProcessingUrl(false);
                    return;
                }

                const cityFindResult = await api.cityFind({
                    lat: matchingDest.center.latitude,
                    lng: matchingDest.center.longitude
                });

                const foundCity = Array.isArray(cityFindResult) ? cityFindResult[0] : cityFindResult;

                if (!foundCity || !foundCity.id) {
                    setProcessingError("Could not find city for this destination");
                    setIsProcessingUrl(false);
                    return;
                }

                const newParams = new URLSearchParams();
                newParams.set("cityId", foundCity.id.toString());
                newParams.set("provider", Model.PROVIDER_ID.VIATOR.toString());
                newParams.set("tourId", parsedTourId);
                if (startDate) newParams.set("startDate", startDate);
                if (endDate) newParams.set("endDate", endDate);
                if (adults) newParams.set("adults", adults.toString());
                if (children) newParams.set("children", children.toString());
                if (lat) newParams.set("lat", lat.toString());
                if (lng) newParams.set("lng", lng.toString());

                setSearchParams(newParams, { replace: true });
                setIsProcessingUrl(false);
                return;
            }

            setProcessingError("Unsupported URL format");
            setIsProcessingUrl(false);
        } catch (error: any) {
            console.error("Error processing external URL:", error);
            setProcessingError(error.message || "Error processing URL");
            setIsProcessingUrl(false);
        }
    }, [fetchDestinations, setSearchParams, startDate, endDate, adults, children]);

    // Process external URL when present
    useEffect(() => {
        if (externalUrl && !tourId) {
            processExternalUrl(externalUrl);
        }
    }, [externalUrl, tourId, processExternalUrl]);

    document.title = CITY_INFO.TITLE(t("tour_details"));

    const handleClose = () => {
        navigate(-1); // Go back to previous page
    };

    // Convert provider string to PROVIDER_ID number
    const providerNumber = provider ? parseInt(provider) : undefined;

    // Show loading while processing Viator URL
    if (isProcessingUrl) {
        return (
            <div>
                <AppNav tripInfoHeader={false} />
                <div className="flex items-center justify-center min-h-screen flex-col gap-4">
                    <PreLoading />
                    <p className="text-gray-600">Processing tour URL...</p>
                </div>
            </div>
        );
    }

    // Show error if processing failed
    if (processingError) {
        return (
            <div>
                <AppNav tripInfoHeader={false} />
                <div className="flex items-center justify-center min-h-screen flex-col gap-4">
                    <p className="text-red-500">{processingError}</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="bg-primary-color text-white px-6 py-2 rounded-lg"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    // If we have an externalUrl but no tourId yet, wait for processing
    if (externalUrl && !tourId) {
        return (
            <div>
                <AppNav tripInfoHeader={false} />
                <div className="flex items-center justify-center min-h-screen flex-col gap-4">
                    <PreLoading />
                    <p className="text-gray-600">Loading tour details...</p>
                </div>
            </div>
        );
    }

    // Victory doesn't require cityId
    const isVictory = providerNumber === Model.PROVIDER_ID.VICTORY;
    if (!provider || !tourId || !providerNumber || (!isVictory && !cityId)) {
        return (
            <div>
                <AppNav
                    tripInfoHeader={false}
                />

                <div className="flex items-center justify-center min-h-screen">
                    <p>Invalid tour details</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            <AppNav
                tripInfoHeader={false}
            />
            <div className="min-h-screen bg-background-color">
                <div className="w-full mx-auto px-4 py-12" style={{padding: '50px 100px 50px 100px'}}>
                    {/* Render tour modal based on provider */}
                    {providerNumber === Model.PROVIDER_ID.VIATOR && cityId && (
                        <VistorTourInfo
                            isOpen={true}
                            tourId={tourId!}
                            cityId={parseInt(cityId)}
                            lat={lat}
                            lng={lng}
                            startDate={startDate}
                            endDate={endDate}
                            adults={adults}
                            children={children}
                            onClose={handleClose}
                        />
                    )}

                    {providerNumber === Model.PROVIDER_ID.VICTORY && (
                        <VictoryProductInfo
                            productId={tourId!}
                            isOpen={true}
                            onClose={handleClose}
                        />
                    )}

                    {providerNumber === Model.PROVIDER_ID.REZDY && (
                        <div className="bg-white rounded-lg shadow-lg p-8">
                            <h2 className="text-2xl font-bold mb-4">Rezdy Tour</h2>
                            <p className="text-gray-600 mb-4">
                                Redirecting to Rezdy booking page...
                            </p>
                            <div className="flex items-center justify-center">
                                <PreLoading />
                            </div>
                        </div>
                    )}

                    {/* Add other providers as needed */}
                    {providerNumber !== Model.PROVIDER_ID.VIATOR &&
                     providerNumber !== Model.PROVIDER_ID.VICTORY &&
                     providerNumber !== Model.PROVIDER_ID.REZDY && (
                        <div className="bg-white rounded-lg shadow-lg p-8">
                            <h2 className="text-2xl font-bold mb-4">Tour Details</h2>
                            <p className="text-gray-600">
                                Provider {provider} - Tour {tourId}
                            </p>
                            <button
                                onClick={handleClose}
                                className="mt-4 bg-primary-color text-white px-6 py-2 rounded-lg hover:bg-primary-color-dark transition-colors"
                            >
                                Go Back
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TourDetailPage;